/**
 * 실패를 신호가 아니라 **원인**으로 다룬다.
 *
 * 지금까지 같은 실패를 세 곳이 각자 해석했다 — 계획이 정규식으로, 실행이 분기로,
 * 사용자 문구가 또 다른 정규식으로. 그래서 새 실패가 올 때마다 세 곳을 새로 팠고,
 * 고친 방식이 매번 달랐다.
 *
 * 여기 적는 것은 **실제로 관찰된 것뿐이다.** 있을 법한 실패를 미리 지어내지 않는다.
 */

export type FailureCause =
  /** 질문에 맞는 화면을 못 찾았다. */
  | 'screen-not-found'
  /** 화면은 열었는데 요구한 열이 그 표에 구조적으로 없다. */
  | 'wrong-screen'
  /** 조회는 됐고 행이 하나도 없다. */
  | 'empty-result'
  /** 행은 있는데 그 열만 값이 전부 비었다. */
  | 'empty-column'
  /** 오류 없이 답이 아닌 값이 나왔다. 계산이 전부 0 이거나 비었다. */
  | 'useless-result'
  /** 화면이 선행조건 알림으로 조회를 막았다. */
  | 'blocked-by-notice'
  /** 선택 칸이나 검색 팝업에서 값을 고르지 못했다. */
  | 'option-not-found'
  /** 초안을 일부만 채웠다. **화면에 이미 행이 있다.** */
  | 'draft-partial'
  /** 게이트웨이·파이썬 서버·모델이 응답하지 않는다. */
  | 'upstream-down'
  /**
   * **ERP 화면이 서버 응답을 못 받았다.** 0행으로 보이지만 자료가 없는 것이 아니다.
   *
   * `upstream-down` 과 섞지 마라 — 그쪽은 AI 쪽이 조용한 것이고 이쪽은 업무 서버다.
   * 사람에게 하는 안내가 달라진다.
   */
  | 'screen-no-answer'
  /** 위 어느 것으로도 못 가른다. */
  | 'unknown';

/**
 * 이 원인을 만난 자리에서 **같은 일을 다시 해도 되는가.**
 *
 * 조회는 몇 번을 해도 화면이 그대로다. 초안은 아니다 — 신규를 두 번 누르면 미저장
 * 행이 둘이 되고, 그것을 사용자가 치워야 한다. 되돌릴 수 없는 것은 재시도하지
 * 않는다는 규칙이 정책의 첫 줄이어야 한다.
 */
export function repeatable(cause: FailureCause): boolean {
  return cause !== 'draft-partial' && cause !== 'option-not-found';
}

/** 사용자의 답을 받아야 넘어갈 수 있는가. 우리가 더 해볼 것이 없다는 뜻이다. */
export function needsUser(cause: FailureCause): boolean {
  return cause === 'option-not-found';
}

type Signal = { name?: string; code?: string; message?: string };

/**
 * 관찰된 신호에서 원인을 고른다.
 *
 * 순서가 뜻을 가진다. 아래로 갈수록 넓은 그물이라, 좁은 것을 먼저 걸러야 한다 —
 * `값이 모두 비어 있다` 가 `컬럼` 이라는 낱말 때문에 화면 규칙에 걸린 적이 있다.
 */
export function classifyFailure(error: unknown): FailureCause {
  const signal = error as Signal;
  if (signal?.name === 'OptionNotFound') return 'option-not-found';

  const text = typeof signal?.message === 'string' ? signal.message : String(error ?? '');
  if (!text.trim()) return 'unknown';

  // 드라이버가 던진 것은 이름으로 가리지만, 답변 문구로 옮겨진 뒤에는 글로만 남는다.
  if (/선택지 .*찾지 못했|검색 팝업에서 .*찾은 것이 없|고를 목록이 없다/.test(text)) {
    return 'option-not-found';
  }
  // **아래 `empty-result` 그물보다 먼저 걸러야 한다.** 화면이 서버 응답을 못 받은
  // 것은 사실이 아니라 **모름**이다. 여기서 새면 사람에게 「자료가 없습니다」라고
  // 단언하게 되고, 사람은 그 말을 믿고 일한다.
  if (/서버 응답을 못 받았다/.test(text)) return 'screen-no-answer';
  // **화면이 늦은 것은 AI 가 끊긴 것이 아니다.** 드라이버의 마감(`조회가 …ms 안에 끝나지
  // 않았다`·`… 안에 열리지 않았다`)을 AI 장애 문구로 옮겼더니, 전자세금계산서발행에서
  // 화면이 바빴던 일이 「AI 서비스에 연결하지 못했습니다」로 나갔다(2026-10-07).
  if (/조회.*안에 끝나지 않았다|안에 열리지 않았다/.test(text)) return 'screen-no-answer';
  if (/HTTP 5\d\d|AI service unavailable/.test(text)) return 'upstream-down';
  if (/초안을 다 채우지 못했|검색 팝업에서 고르지 못했|화면 상태에 반영되지 않았/.test(text)) {
    return 'draft-partial';
  }
  if (/선행조건 알림으로 차단|기능 없음|할 수 없습니다/.test(text)) return 'blocked-by-notice';
  // **없는 것을 「처리하지 못했다」로 말하지 않는다.** 표 안 버튼을 누르려는데 그
  // 행이 없으면 그것은 고장이 아니라 **사실**이다 — 사람이 「덕봉빌딩 점검일지
  // 열어줘」라고 했는데 그 달 덕봉빌딩 점검이 없는 경우다(2026-09-22 실측).
  // 「다시 한 번 물어봐 주세요」는 그 자리에서 아무 도움이 안 된다.
  if (/표가 비었다|누를 행을 찾지 못했다/.test(text)) return 'empty-result';
  // 여럿이 걸렸으면 사람이 골라 줘야 넘어간다.
  if (/누를 행이 \d+개다/.test(text)) return 'option-not-found';
  if (/값이 모두 비어 있다/.test(text)) return 'empty-column';
  if (/결과가 비어 있다|행이 없다|행이 늘지 않았다/.test(text)) return 'empty-result';
  if (/필수 컬럼 \S+ 이 없다|표에 필수 컬럼|열이 없다|표가 없다/.test(text)) return 'wrong-screen';
  if (/화면 목록에 없다|스킬|업무 도구|계약|분류 기준|결과 표에 없다/.test(text)) {
    return 'screen-not-found';
  }
  return 'unknown';
}

/** 조회 검증이 낸 코드를 원인으로 옮긴다. 문자열을 다시 읽지 않는다. */
export function causeOfIssue(
  code: 'empty-result' | 'missing-column' | 'empty-column' | 'no-answer',
): FailureCause {
  if (code === 'empty-column') return 'empty-column';
  if (code === 'missing-column') return 'wrong-screen';
  // 서버가 대답을 안 한 것은 **장애**다. 자료가 없는 것이 아니다.
  if (code === 'no-answer') return 'screen-no-answer';
  return 'empty-result';
}
